import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ClipboardCheck, ChevronRight, TrendingUp, TrendingDown, Minus } from "lucide-react";

const STAGES = [["baseline", "Baseline"], ["week6", "Week 6"], ["week12", "Week 12"]];
const ROWS = [
  ["energy", "Energy (1–5)", "num"], ["capability", "Everyday capability (1–5)", "num"],
  ["walking_baseline", "Daily walking", "text"], ["session_consistency", "Lifting consistency", "text"], ["strength_benchmark", "Strength benchmark", "text"],
  ["body_weight_kg", "Body weight (kg)", "num"], ["waist_cm", "Waist (cm)", "num"],
  ["goal", "Healthspan goal", "text"], ["reflection", "Reflection", "text"],
];

function Trend({ a, b, lowerIsBetter }) {
  if (a == null || b == null || a === b) return <Minus size={12} className="text-muted-foreground inline" />;
  const up = b > a;
  const good = lowerIsBetter ? !up : up;
  const Icon = up ? TrendingUp : TrendingDown;
  return <Icon size={12} className={`inline ${good ? "text-emerald-600" : "text-muted-foreground"}`} />;
}

export function ReviewComparison() {
  const [reviews, setReviews] = useState(null);
  useEffect(() => { api.get("/reviews").then((r) => setReviews(r.data)).catch(() => setReviews({})); }, []);
  if (!reviews) return null;
  const have = STAGES.filter(([s]) => reviews[s]);
  const missing = STAGES.filter(([s]) => !reviews[s]);

  return (
    <div className="bg-card border border-border rounded-lg p-6" data-testid="review-comparison">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="label-eyebrow">Foundation reviews</div>
          <div className="font-head uppercase text-xl mt-1">Progress beyond the scales</div>
        </div>
        {missing.length > 0 && (
          <Link to={`/app/review/${missing[0][0]}`} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest border border-border px-3 py-2 rounded hover:bg-muted min-h-[44px]" data-testid="review-comparison-add">
            <ClipboardCheck size={12} /> Add {missing[0][1]} review <ChevronRight size={12} />
          </Link>
        )}
      </div>
      {have.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">Capture a Baseline review now, then Week 6 and Week 12. You'll see your energy, capability, walking and strength side by side — not just a number on a scale.</p>
      ) : (
        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm" data-testid="review-comparison-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                <th className="py-2 pr-4 font-normal">Measure</th>
                {STAGES.map(([s, label]) => <th key={s} className="py-2 pr-4 font-normal">{label}</th>)}
                <th className="py-2 font-normal">Trend</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {ROWS.map(([k, label, type]) => {
                const vals = STAGES.map(([s]) => reviews[s]?.[k]);
                if (vals.every((v) => v == null || v === "")) return null;
                const nums = vals.filter((v) => v != null && v !== "");
                return (
                  <tr key={k} data-testid={`review-row-${k}`}>
                    <td className="py-3 pr-4 text-muted-foreground align-top">{label}</td>
                    {vals.map((v, i) => <td key={i} className={`py-3 pr-4 align-top ${type === "text" ? "max-w-[220px] text-xs leading-relaxed" : "font-head"}`}>{v ?? "—"}</td>)}
                    <td className="py-3 align-top">{type === "num" && nums.length >= 2 ? <Trend a={Number(nums[0])} b={Number(nums[nums.length - 1])} lowerIsBetter={k === "waist_cm"} /> : null}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
